import { customElement } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { BaseShape } from './base-shape.js'
import { isGeoJSONSource } from '../map-engine.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import { db } from '../../../database/schema.js'
import { getFillPaintProperties } from './fill-patterns.js'
import {
  haversineDistance,
  calculateDestinationPoint,
  circleToGeoJSONCoords,
} from '../geo-utils.js'
import { addShapeLayer } from '../layer-order-manager.js'

@customElement('shape-circle')
export class ShapeCircle extends BaseShape {
  private radiusHandle?: maplibregl.Marker

  private circleToPolygon(center: [number, number], radius: number, steps = 64): number[][] {
    return circleToGeoJSONCoords(center, radius, steps)
  }

  protected createLayers(): void {
    const coords = (this.feature.geometry as GeoJSON.Point).coordinates
    const center: [number, number] = [coords[0], coords[1]]
    const radius =
      this.feature.style?.radius ?? (this.feature.properties?.radius as number | undefined) ?? 100
    const circleCoords = this.circleToPolygon(center, radius)

    this.map.addSource(this.sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [circleCoords] },
      },
    })

    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')
    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`

    addShapeLayer(this.map, {
      id: fillLayerId,
      type: 'fill',
      source: this.sourceId,
      paint: { 'fill-color': fillProps.fillColor, 'fill-opacity': fillProps.fillOpacity },
    })

    const dashArray = this.feature.style?.strokeDashArray ?? undefined
    addShapeLayer(this.map, {
      id: strokeLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': this.feature.style?.strokeColor ?? '#3f5a7a',
        'line-width': this.feature.style?.strokeWidth ?? 2,
        'line-opacity': this.feature.style?.strokeOpacity ?? 0.8,
        ...(dashArray ? { 'line-dasharray': dashArray } : {}),
      },
    })

    this.layerIds = [fillLayerId, strokeLayerId]
  }

  getGeoJSON(): GeoJSON.Feature {
    const coords = (this.feature.geometry as GeoJSON.Point).coordinates
    const center: [number, number] = [coords[0], coords[1]]
    const radius =
      this.feature.style?.radius ?? (this.feature.properties?.radius as number | undefined) ?? 100
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [this.circleToPolygon(center, radius)] },
    }
  }

  updateSource(): void {
    const source = this.map.getSource(this.sourceId)
    if (isGeoJSONSource(source)) {
      source.setData(this.getGeoJSON())
    }
  }

  override refreshStyle(): void {
    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`
    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')
    if (this.map.getLayer(fillLayerId)) {
      this.map.setPaintProperty(fillLayerId, 'fill-color', fillProps.fillColor)
      this.map.setPaintProperty(fillLayerId, 'fill-opacity', fillProps.fillOpacity)
    }
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-color',
        this.feature.style?.strokeColor ?? '#3f5a7a'
      )
      this.map.setPaintProperty(strokeLayerId, 'line-width', this.feature.style?.strokeWidth ?? 2)
      this.map.setPaintProperty(
        strokeLayerId,
        'line-opacity',
        this.feature.style?.strokeOpacity ?? 0.8
      )
      const dashArray = this.feature.style?.strokeDashArray
      this.map.setPaintProperty(
        strokeLayerId,
        'line-dasharray',
        dashArray?.length ? dashArray : (null as number[] | null)
      )
    }
    this.applyFillPatternToLayer()
  }

  protected createHandles(): void {
    const coords = (this.feature.geometry as GeoJSON.Point).coordinates
    const center: [number, number] = [coords[0], coords[1]]
    const radius =
      this.feature.style?.radius ?? (this.feature.properties?.radius as number | undefined) ?? 100

    const radiusLngLat = calculateDestinationPoint(
      new maplibregl.LngLat(center[0], center[1]),
      radius,
      90
    )
    this.radiusHandle = this.createHandle([radiusLngLat.lng, radiusLngLat.lat], 'radius')
    this.handles.push(this.radiusHandle)

    this.setupCircleDragging()
  }

  protected override setupHandleDragging(marker: maplibregl.Marker, handleType: string): void {
    const element = marker.getElement()
    element.addEventListener('mousedown', e => {
      e.stopPropagation()
      e.preventDefault()
      this.startHandleDrag(marker, handleType)
    })
    element.addEventListener(
      'touchstart',
      e => {
        e.preventDefault()
        e.stopPropagation()
        this.startHandleDrag(marker, handleType)
      },
      { passive: false }
    )
  }

  protected updateShapePreview(handleType: string, newPosition: maplibregl.LngLat): void {
    if (handleType === 'drag') {
      const radius =
        this.feature.style?.radius ?? (this.feature.properties?.radius as number | undefined) ?? 100
      const center: [number, number] = [newPosition.lng, newPosition.lat]
      const circleCoords = this.circleToPolygon(center, radius)
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [circleCoords] },
        })
      }
    } else if (handleType === 'radius') {
      const center = (this.feature.geometry as GeoJSON.Point).coordinates
      const newRadius = haversineDistance(center[1], center[0], newPosition.lat, newPosition.lng)
      const circleCoords = this.circleToPolygon([center[0], center[1]], newRadius)
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [circleCoords] },
        })
      }
    }
  }

  protected async applyGeometryChange(
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    if (handleType === 'drag') {
      await tripFeatureManager.moveFeature(this.feature.id, {
        lat: newPosition.lat,
        lng: newPosition.lng,
      })
      ;(this.feature.geometry as GeoJSON.Point).coordinates = [newPosition.lng, newPosition.lat]
    } else if (handleType === 'radius') {
      const center = (this.feature.geometry as GeoJSON.Point).coordinates
      const newRadius = haversineDistance(center[1], center[0], newPosition.lat, newPosition.lng)
      await Promise.all([
        tripFeatureManager.applyFeatureStyle(this.feature.id, {
          ...this.feature.style,
          radius: newRadius,
        }),
        db.features.update(this.feature.id, {
          properties: { ...this.feature.properties, radius: newRadius },
          updatedAt: new Date().toISOString(),
        }),
      ])
      if (this.feature.style) {
        this.feature.style.radius = newRadius
      }
      if (this.feature.properties) {
        this.feature.properties.radius = newRadius
      }
    }
  }

  protected updateAllHandlePositions(centerPosition: maplibregl.LngLat): void {
    if (this.radiusHandle) {
      const radius =
        this.feature.style?.radius ?? (this.feature.properties?.radius as number | undefined) ?? 100
      const radiusLngLat = calculateDestinationPoint(
        new maplibregl.LngLat(centerPosition.lng, centerPosition.lat),
        radius,
        90
      )
      this.radiusHandle.setLngLat(radiusLngLat)
    }
  }

  protected override applySelectionStyle(selected: boolean): void {
    const strokeLayerId = `${this.sourceId}-stroke`
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-width',
        selected
          ? (this.feature.style?.strokeWidth ?? 2) + 2
          : (this.feature.style?.strokeWidth ?? 2)
      )
    }
  }

  private setupCircleDragging(): void {
    const fillLayerId = `${this.sourceId}-fill`
    this.map.on('mousedown', fillLayerId, e => {
      if (!this.isEditing) {
        return
      }
      e.preventDefault()
      this.startShapeDrag(e.lngLat)
    })
  }

  private _dragMouseMove?: ((e: MouseEvent) => void) | undefined
  private _dragMouseUp?: ((e: MouseEvent) => void) | undefined
  private _dragTouchMove?: ((e: TouchEvent) => void) | undefined
  private _dragTouchEnd?: ((e: TouchEvent) => void) | undefined
  private startShapeDrag(_startPosition: maplibregl.LngLat): void {
    if (this.isDragging) {
      return
    }
    this.isDragging = true
    this.handles.forEach(h => (h.getElement().style.display = 'none'))
    this._dragMouseMove = (e: MouseEvent) => {
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      this.updateShapePreview('drag', lngLat)
    }
    this._dragMouseUp = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      void this.endShapeDrag(lngLat)
    }
    this._dragTouchMove = (e: TouchEvent) => {
      if (!this.isDragging || e.touches.length !== 1) {
        return
      }
      const lngLat = this.touchToLngLat(e.touches[0])
      e.preventDefault()
      this.updateShapePreview('drag', lngLat)
    }
    this._dragTouchEnd = (e: TouchEvent) => {
      if (!this.isDragging) {
        return
      }
      const lngLat = e.changedTouches.length > 0 ? this.touchToLngLat(e.changedTouches[0]) : null
      if (!lngLat) {
        return
      }
      e.preventDefault()
      void this.endShapeDrag(lngLat)
    }
    document.addEventListener('mousemove', this._dragMouseMove)
    document.addEventListener('mouseup', this._dragMouseUp)
    this.map.getContainer().addEventListener('touchmove', this._dragTouchMove, { passive: false })
    this.map.getContainer().addEventListener('touchend', this._dragTouchEnd, { passive: false })
  }

  private async endShapeDrag(newPosition: maplibregl.LngLat): Promise<void> {
    if (!this.isDragging) {
      return
    }
    this.isDragging = false
    BaseShape.ignoreNextMapClick++
    setTimeout(() => {
      if (BaseShape.ignoreNextMapClick > 0) {
        BaseShape.ignoreNextMapClick = 0
      }
    }, 200)
    if (this._dragMouseMove) {
      document.removeEventListener('mousemove', this._dragMouseMove)
    }
    if (this._dragMouseUp) {
      document.removeEventListener('mouseup', this._dragMouseUp)
    }
    if (this._dragTouchMove) {
      this.map.getContainer().removeEventListener('touchmove', this._dragTouchMove)
    }
    if (this._dragTouchEnd) {
      this.map.getContainer().removeEventListener('touchend', this._dragTouchEnd)
    }
    this._dragMouseMove = this._dragMouseUp = this._dragTouchMove = this._dragTouchEnd = undefined
    await this.applyGeometryChange('drag', newPosition)
    if (this.isEditing) {
      this.removeHandles()
      this.createHandles()
    }
  }
}
